import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { ensureFreshToken, getAccessToken, redirectToKeycloakLogin } from '@/plugins/vuekeycloak'

type RetriableConfig = InternalAxiosRequestConfig & { _authRetry?: boolean }

/**
 * Ajoute le jeton Keycloak à chaque requête et rejoue une fois la requête après un 401
 * (jeton invalidé côté serveur alors qu'il paraissait encore valide côté client).
 */
export function attachAuthInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use(
    async (config) => {
      const token = await getAccessToken()
      if (token) config.headers['Authorization'] = `Bearer ${token}`
      return config
    },
    (error) => Promise.reject(error),
  )

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config as RetriableConfig | undefined
      if (error?.response?.status !== 401 || !config || config._authRetry) {
        return Promise.reject(error)
      }

      config._authRetry = true
      // `-1` force un renouvellement même si le jeton courant n'est pas encore expiré.
      const refreshed = await ensureFreshToken(-1)
      if (!refreshed) {
        redirectToKeycloakLogin()
        return Promise.reject(error)
      }

      const token = await getAccessToken()
      if (token) config.headers['Authorization'] = `Bearer ${token}`
      return instance.request(config)
    },
  )
}
